import React from 'react';
import { UserRound, Users, GraduationCap } from 'lucide-react-native';
import { STORAGE_KEYS } from '../../../common/auth/storage';
import { authImpersonateUser } from '../../../common/api/endpoints/auth/auth';
import CohortSelector from '../CohortSelector';
import CourseSelector from '../CourseSelector';
import type { ActionHandlerProps, ActionConfig } from '../DataTable/actionTypes';
import log from '../../../common/utils/logger';
import { User } from '../../../common/api/models';

export const userActions: Record<string, Partial<ActionConfig>> = {
  enroll: {
    id: 'enroll',
    label: 'Enroll in Course',
    icon: GraduationCap,
    position: 'bulk',
    bulk: true,
    availCondition: (data) => {
      // Only available for student users
      if (Array.isArray(data)) {
        return data.length > 0 && (data as User[]).every((u) => u.role === 'student');
      }
      return (data as User)?.role === 'student';
    },
    handler: ({ data, utils }: ActionHandlerProps) => {
      const {
        dataProvider, showDialog, hideDialog, showToastMessage, setRefreshKey, queryClient,
      } = utils;

      const isBulk = Array.isArray(data);
      const items = (isBulk ? (data as User[]) : [data as User]);
      const count = items.length;

      showDialog({
        title: isBulk ? `Enroll ${count} Students` : 'Enroll Student',
        message: 'Select the course to enroll the student(s) in:',
        children: (
          <CourseSelector
            onCancel={hideDialog}
            onConfirm={async (courseId: string) => {
              try {
                utils.setActionRunning(true);
                const promises = items.map((item) => dataProvider.linkItems('courses', courseId, 'users', item.id));
                await Promise.all(promises);
                showToastMessage(
                  'success',
                  isBulk ? `Successfully enrolled ${count} students` : 'Student enrolled successfully',
                );
                hideDialog();

                // Invalidate both users and courses to ensure fresh data and sidebar/count updates
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: ['/users'] });
                  queryClient.invalidateQueries({ queryKey: ['/courses'] });
                }

                setRefreshKey(Date.now());
              } catch (error: unknown) {
                log.error('Enrollment error:', error);
                showToastMessage('error', `Failed to enroll: ${(error as Error).message}`);
              } finally {
                utils.setActionRunning(false);
              }
            }}
          />
        ),
        buttons: [],
      });
    },
  },
  putInCohort: {
    id: 'putInCohort',
    label: 'Put in Cohort',
    icon: Users,
    position: 'bulk',
    bulk: true,
    availCondition: (data) => {
      // Only available for student users
      if (Array.isArray(data)) {
        return (data as User[]).every((u) => u.role === 'student');
      }
      return (data as User)?.role === 'student';
    },
    handler: ({ data, mutations, utils }: ActionHandlerProps) => {
      const { updateMutation } = mutations;
      const {
        showDialog, hideDialog, showToastMessage, setRefreshKey, queryClient,
      } = utils;

      const isBulk = Array.isArray(data);
      const items = (isBulk ? (data as User[]) : [data as User]);
      const count = items.length;

      showDialog({
        title: isBulk ? `Assign ${count} Students to Cohort` : 'Assign Student to Cohort',
        message: 'Select the cohort for the selected student(s):',
        children: (
          <CohortSelector
            onCancel={hideDialog}
            onConfirm={async (cohortId: string) => {
              try {
                utils.setActionRunning(true);
                const promises = items.map((item) => updateMutation.mutateAsync({
                  ...item,
                  cohort: { id: cohortId },
                }));
                await Promise.all(promises);
                showToastMessage(
                  'success',
                  isBulk ? `Cohort updated for ${count} students` : 'Cohort updated successfully',
                );
                hideDialog();

                // Final invalidation after bulk operation
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: ['/users'] });
                }

                setRefreshKey(Date.now());
              } catch (error: unknown) {
                log.error('Assign cohort error:', error);
                showToastMessage('error', `Failed to update cohort: ${(error as Error).message}`);
              } finally {
                utils.setActionRunning(false);
              }
            }}
          />
        ),
        buttons: [],
      });
    },
  },
  impersonate: {
    id: 'impersonate',
    label: 'Impersonate',
    icon: UserRound,
    position: 'row',
    availCondition: (data) => (data as User)?.role !== 'admin',
    handler: async ({ data, utils }: ActionHandlerProps) => {
      const { showDialog, showToastMessage } = utils;
      const item = data as User;

      showDialog({
        title: 'Impersonate User',
        message: `Are you sure you want to log in as ${item.firstname} ${item.lastname}? This will open a new browser window.`,
        buttons: [
          { text: 'Cancel', variant: 'outline', action: 'secondary' },
          {
            text: 'Impersonate',
            action: 'primary',
            onPress: async () => {
              // Safari/Pop-up blockers: Open the window immediately in response to user click
              let newWindow: Window | null = null;
              if (typeof window !== 'undefined' && window.open) {
                newWindow = window.open('about:blank', '_blank');
              }

              try {
                // Perform impersonation to get tokens
                const response = await authImpersonateUser(item.id);

                const loginData = response.data;
                if (!loginData || !loginData.data) {
                  throw new Error('Invalid response from server during impersonation');
                }

                const { accessToken, refreshToken } = loginData.data;

                // Option 1: Handoff via LocalStorage
                const handoffData = {
                  accessToken,
                  refreshToken,
                  timestamp: Date.now(),
                };

                // Use a specific key for handoff
                localStorage.setItem(STORAGE_KEYS.IMPERSONATION_HANDOFF, JSON.stringify(handoffData));

                // Now that tokens are set, redirect the already-opened window
                if (newWindow) {
                  newWindow.location.href = '/';
                  showToastMessage('success', `Opening session for ${item.username || item.id}...`);
                } else {
                  showToastMessage('error', 'Impersonation is only supported on web');
                }
              } catch (error: unknown) {
                log.error('Impersonation error:', error);
                if (newWindow) newWindow.close();
                showToastMessage('error', `Failed to impersonate: ${(error as Error).message}`);
              }
            },
          },
        ],
      });
    },
  },
};
